/**
 * Iter208.66 — Wizard di creazione sito pubblico per il PRO.
 *
 * 4 step:
 *   1) Logo (upload OR generazione one-shot via Gemini Nano Banana)
 *   2) Colori (primary, secondary, text) con preview live
 *   3) Template (5 per categoria, colori applicati dinamicamente)
 *   4) Pubblica (riepilogo + POST /site/publish)
 *
 * Il rendering del sito pubblico su dominio custom sarà cablato in una fase
 * successiva. Qui ci limitiamo a raccogliere le preferenze e persisterle.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import TemplateLivePreviewModal from "@/components/TemplateLivePreviewModal";
import {
  CheckCircle, CaretLeft, CaretRight, UploadSimple, MagicWand,
  Palette, Layout, Rocket, ArrowRight, Lock, Info,
  Desktop, DeviceTablet, DeviceMobile, MagnifyingGlass, ArrowSquareOut,
} from "@phosphor-icons/react";

// ==================== TEMPLATE SKELETONS ====================
// Iter209i — Solo 5 template ufficiali (Atrium, Casa Vicina, Villa Reale,
// Meridian, Bottega Case). Rimossi: Classic, Modern, Luxury, Minimal, Vibrant
// (agenzia); Blueprint, Corporate, Portfolio, Industrial, Airy (tecnica);
// Heritage, Moderno, Editorial, Formal, Warm (notarile). Gli stessi 5
// template vengono proposti a tutte le categorie di pro.
const OFFICIAL_TEMPLATES = [
  { id: "atrium-editorial", label: "Atrium", desc: "Editorial luxury con palette verdigris/clay, tipografia Fraunces/Archivo, 4 pagine complete" },
  { id: "casa-vicina",      label: "Casa Vicina", desc: "Playful e caldo, palette coral/leaf/sun, tipografia Bricolage Grotesque, menu overlay a tutto schermo" },
  { id: "villa-reale",      label: "Villa Reale", desc: "Editorial classico immobili di pregio, palette bordeaux/brass/cream, sidebar fissa e tipografia Cormorant Garamond" },
  { id: "meridian",         label: "Meridian", desc: "Modern/tech, palette ink/accent/mint, pill nav flottante, bento grid con mappa e cta-card sopra footer scuro" },
  { id: "bottega-case",     label: "Bottega Case", desc: "Crafty/scrapbook, palette moss/rust/mustard/paper, polaroid ruotate, masonry taped, sticky-note e postcard, Alfa Slab One/Caveat" },
];
const TEMPLATES = {
  agenzia:  OFFICIAL_TEMPLATES,
  tecnica:  OFFICIAL_TEMPLATES,
  notarile: OFFICIAL_TEMPLATES,
};

const CATEGORY_LABEL = {
  agenzia: "Agenzia Immobiliare",
  tecnica: "Studio Tecnico",
  notarile: "Studio Notarile",
};

const PAGES = {
  agenzia:  ["Home", "Annunci", "Chi siamo", "Contatti"],
  tecnica:  ["Home", "Chi siamo", "Contatti"],
  notarile: ["Chi siamo", "Prenota appuntamento", "Contatti"],
};

const DEFAULT_COLORS = { primary: "#6d28d9", secondary: "#f5f3ff", text: "#0f172a" };

// ==================== TemplatePreview ====================
// Piccolo mockup SVG-like che rispecchia lo stile del template usando le
// CSS custom properties: --c-primary, --c-secondary, --c-text.
function TemplatePreview({ template, colors, studioName, logoUrl, small = false }) {
  const cssVars = {
    "--c-primary": colors.primary,
    "--c-secondary": colors.secondary,
    "--c-text": colors.text,
  };
  const style = template.id.split("-")[1];
  // Iter208.80 — Bottega Case: preview scrapbook con polaroid ruotate + palette moss/rust/mustard
  if (template.id === "bottega-case") {
    return (
      <div
        className={"overflow-hidden shadow-sm border-2 " + (small ? "text-[8px]" : "text-xs")}
        style={{ background: "#F3ECDD", color: "#2B2318", borderColor: "#2B2318", fontFamily: "'Nunito Sans', sans-serif" }}
        data-testid="site-wizard-preview-bottega-case"
      >
        <div className="px-2 py-1 flex justify-between" style={{ background: "#414E32", color: "#E7E0CC", fontSize: small ? 5 : 6 }}>
          <span>✆ 051 · ✉ ciao</span><span>IG · FB</span>
        </div>
        <div className="px-2 py-1 flex items-center justify-between border-b-2" style={{ background: "#F3ECDD", borderColor: "#2B2318" }}>
          <span style={{ fontSize: small ? 5 : 7, fontWeight: 600 }}>Home · Annunci</span>
          <span style={{ fontFamily: "'Alfa Slab One',serif", fontSize: small ? 7 : 10 }}>{studioName || "Bottega"}<span style={{ color: "#B5502D" }}>.</span></span>
          <span className="border-2 border-dashed px-1 rounded-full" style={{ borderColor: "#2B2318", fontSize: small ? 4 : 5, transform: "rotate(-4deg)", background: "#F3ECDD" }}>Contatti ✎</span>
        </div>
        <div className="px-2 py-2 grid grid-cols-2 gap-2">
          <div>
            <span style={{ fontFamily: "'Caveat',cursive", fontSize: small ? 6 : 8, color: "#B5502D" }}>Ciao,</span>
            <div style={{ fontFamily: "'Alfa Slab One',serif", fontSize: small ? 7 : 10, lineHeight: 1.1 }}>Le case sono storie.</div>
            <div className="mt-1 inline-block rounded-full px-1.5 py-0.5" style={{ background: "#B5502D", color: "#FBF6EA", border: "1.5px solid #2B2318", fontSize: small ? 4 : 6, fontWeight: 700, boxShadow: "2px 2px 0 #2B2318" }}>Sfoglia →</div>
          </div>
          <div className="relative" style={{ height: small ? 26 : 40 }}>
            <div className="absolute p-0.5 bg-white border" style={{ borderColor: "#2B2318", top: 0, left: 0, width: "45%", height: "60%", transform: "rotate(-7deg)", background: "#5B6B45" }}/>
            <div className="absolute p-0.5 bg-white border" style={{ borderColor: "#2B2318", top: "20%", right: 0, width: "45%", height: "75%", transform: "rotate(5deg)", background: "#B5502D" }}/>
            <div className="absolute p-0.5 bg-white border" style={{ borderColor: "#2B2318", bottom: 0, left: "20%", width: "40%", height: "50%", transform: "rotate(3deg)", background: "#E3A63A" }}/>
          </div>
        </div>
      </div>
    );
  }
  // Iter208.79 — Meridian: preview con pill nav flottante + bento grid dark accent blue
  if (template.id === "meridian") {
    return (
      <div
        className={"overflow-hidden shadow-sm border " + (small ? "text-[8px]" : "text-xs")}
        style={{ background: "#F4F7F7", color: "#0B1F2A", borderColor: "#DCE4E4", fontFamily: "'Manrope', sans-serif" }}
        data-testid="site-wizard-preview-meridian"
      >
        <div className="flex justify-center py-1">
          <div className="flex items-center gap-1 rounded-full px-1.5 py-0.5" style={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(11,31,42,0.1)" }}>
            <span className="rounded-full inline-block" style={{ background: "#2E7DFF", width: small ? 4 : 5, height: small ? 4 : 5 }} />
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: small ? 6 : 8 }}>{studioName || "Meridian"}</span>
            <span className="rounded-full px-1" style={{ background: "#0B1F2A", color: "#fff", fontSize: small ? 4 : 6 }}>Valuta</span>
          </div>
        </div>
        <div className="mx-1 rounded-lg overflow-hidden relative" style={{ background: "#0E2A3B", height: small ? 30 : 46 }}>
          <div className="absolute inset-0 grid grid-cols-4 gap-0.5 opacity-30">
            {Array(8).fill(0).map((_, i) => <div key={i} className="border" style={{ borderColor: "#1D4A61" }}/>)}
          </div>
          <div className="absolute inset-0 flex items-center px-2">
            <div className="rounded p-1" style={{ background: "rgba(11,31,42,0.7)", color: "#fff", fontSize: small ? 5 : 7, backdropFilter: "blur(6px)" }}>
              <div style={{ color: "#8FD9C4", fontSize: small ? 3 : 4 }}>MAPPA</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700 }}>Casa geolocalizzata</div>
            </div>
          </div>
          <div className="absolute rounded px-1" style={{ top: 4, right: small ? 10 : 20, background: "#2E7DFF", color: "#fff", fontSize: small ? 4 : 6 }}>€340k</div>
          <div className="absolute rounded px-1" style={{ top: small ? 18 : 26, right: small ? 4 : 8, background: "#1FAE8B", color: "#fff", fontSize: small ? 4 : 6 }}>€260k</div>
        </div>
        <div className="grid grid-cols-4 gap-0.5 p-1" style={{ height: small ? 30 : 44 }}>
          <div className="col-span-2 row-span-2 rounded" style={{ background: "#2E7DFF" }}/>
          <div className="rounded" style={{ background: "#1FAE8B" }}/>
          <div className="rounded" style={{ background: "#0E2A3B" }}/>
          <div className="col-span-2 rounded" style={{ background: "#5FA8FF" }}/>
        </div>
      </div>
    );
  }
  // Iter208.78 — Villa Reale: preview con sidebar bordeaux + hero dark ornamentale
  if (template.id === "villa-reale") {
    return (
      <div
        className={"overflow-hidden shadow-sm border " + (small ? "text-[8px]" : "text-xs")}
        style={{ background: "#FBF6EE", color: "#2B2622", borderColor: "#E2D6C0", fontFamily: "'Jost', sans-serif" }}
        data-testid="site-wizard-preview-villa-reale"
      >
        <div className="flex" style={{ height: small ? 60 : 96 }}>
          <div className="flex flex-col justify-between p-1.5" style={{ background: "#5C1A2A", color: "#F1E8D8", width: small ? 40 : 60 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: small ? 6 : 8, color: "#FBF6EE", lineHeight: 1 }}>
              {(studioName || "Villa Reale").split(" ").slice(0, 2).join(" ")}
              <div style={{ fontSize: small ? 3 : 4, letterSpacing: 1, color: "#C9A15A", textTransform: "uppercase", marginTop: 1 }}>Immobiliare</div>
            </div>
            <div className="space-y-0.5" style={{ fontSize: small ? 4 : 5, fontFamily: "'Cormorant Garamond', serif" }}>
              <div>Home</div><div>Annunci</div><div>Contatti</div>
            </div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center text-center" style={{ background: "#3E1019", color: "#FBF6EE", padding: small ? 4 : 8 }}>
            <div style={{ fontSize: small ? 3 : 5, letterSpacing: 2, textTransform: "uppercase", color: "#C9A15A" }}>Dal 1998</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: small ? 8 : 12, marginTop: 2, lineHeight: 1.05 }}>Proprietà di pregio</div>
            <div className="mt-1 px-1.5 py-0.5" style={{ background: "#C9A15A", color: "#3E1019", fontSize: small ? 3 : 5, letterSpacing: 1, textTransform: "uppercase" }}>Sfoglia</div>
          </div>
        </div>
        <div className="px-2 py-1.5 grid grid-cols-2 gap-1.5" style={{ background: "#FBF6EE" }}>
          <div className="border" style={{ borderColor: "#5C1A2A", height: small ? 10 : 16 }} />
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: small ? 6 : 9, color: "#5C1A2A" }}>€ 890.000</div>
        </div>
      </div>
    );
  }
  // Iter208.77 — Casa Vicina: preview con palette coral/sun/leaf + tipografia grassa
  if (template.id === "casa-vicina") {
    return (
      <div
        className={"overflow-hidden shadow-sm border-2 " + (small ? "text-[8px]" : "text-xs")}
        style={{ background: "#FFFFFF", color: "#132A2A", borderColor: "#132A2A", fontFamily: "'Work Sans', sans-serif" }}
        data-testid="site-wizard-preview-casa-vicina"
      >
        <div className="flex items-center justify-between px-2 py-1.5 border-b-2" style={{ borderColor: "#132A2A" }}>
          <div className="flex items-center gap-1" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: small ? 8 : 11 }}>
            <span className="rounded-full inline-block" style={{ background: "#FF6B4A", width: small ? 5 : 7, height: small ? 5 : 7 }} />
            {studioName || "Casa Vicina"}
          </div>
          <div className="flex items-center gap-1">
            <div className="border-2" style={{ background: "#FFC94A", borderColor: "#132A2A", width: small ? 12 : 16, height: small ? 8 : 12 }} />
          </div>
        </div>
        <div className="px-2 py-2.5">
          <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: small ? 8 : 12, lineHeight: 1.05 }}>
            Trovare casa non deve essere complicato.
          </div>
          <div className="grid grid-cols-3 gap-0.5 mt-1.5">
            <div className="border-2" style={{ background: "#2F6B4F", borderColor: "#132A2A", height: small ? 12 : 20 }} />
            <div className="border-2 relative" style={{ background: "#FFC94A", borderColor: "#132A2A", height: small ? 10 : 16 }}>
              <span className="absolute -top-0.5 -right-0.5 border-2 px-0.5" style={{ background: "#FFC94A", borderColor: "#132A2A", fontSize: small ? 4 : 5, fontFamily: "'Bricolage Grotesque'", fontWeight: 800, transform: "rotate(-6deg)" }}>340k</span>
            </div>
            <div className="border-2" style={{ background: "#FF6B4A", borderColor: "#132A2A", height: small ? 14 : 22 }} />
          </div>
        </div>
        <div className="px-2 py-1 flex gap-1" style={{ background: "#132A2A", color: "#fff" }}>
          {["Zona", "Tipo", "€"].map((l) => (
            <div key={l} className="flex-1 border-b" style={{ borderColor: "#3E5C54", fontSize: small ? 5 : 6, fontFamily: "'Space Mono', monospace", textTransform: "uppercase", opacity: 0.7 }}>
              {l}
            </div>
          ))}
          <div className="border-2 px-1" style={{ background: "#FF6B4A", borderColor: "#132A2A", color: "#fff", fontSize: small ? 5 : 7, fontWeight: 700 }}>Cerca</div>
        </div>
      </div>
    );
  }
  // Iter208.76 — Atrium editorial: mini-mock dedicato (palette verdigris/paper).
  if (template.id === "atrium-editorial") {
    return (
      <div
        className={"rounded-lg overflow-hidden border border-slate-200 shadow-sm " + (small ? "text-[8px]" : "text-xs")}
        style={{ background: "#F3EFE6", color: "#14171A", fontFamily: "'Fraunces', Georgia, serif" }}
        data-testid="site-wizard-preview-atrium"
      >
        <div className="flex items-center justify-between px-2 py-1.5 border-b" style={{ background: "#F3EFE6", borderColor: "#D9D2C1" }}>
          <div className="flex items-center gap-1.5">
            {logoUrl ? <img src={logoUrl} alt="logo" className={small ? "h-3 w-3 rounded" : "h-4 w-4 rounded object-cover"} /> : <div className={(small ? "h-3 w-3" : "h-4 w-4") + " rounded"} style={{ background: "#6E8C7C" }} />}
            <span className="font-bold" style={{ fontSize: small ? 7 : 10 }}>
              {(studioName || "ATRIUM").toUpperCase()}<span style={{ color: "#6E8C7C" }}>.</span>
            </span>
          </div>
          <div className="flex gap-1 opacity-60" style={{ fontSize: small ? 6 : 8, fontFamily: "'IBM Plex Mono', monospace" }}>
            <span>Home</span><span>Annunci</span><span>Contatti</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-0" style={{ background: "#14171A", color: "#F3EFE6" }}>
          <div className="p-2">
            <div className="uppercase tracking-widest" style={{ fontSize: small ? 5 : 7, color: "#6E8C7C", fontFamily: "'IBM Plex Mono', monospace" }}>Dal 1998</div>
            <div className="font-semibold mt-1 leading-tight" style={{ fontFamily: "'Fraunces', serif", fontSize: small ? 9 : 12 }}>
              Case che raccontano una storia.
            </div>
            <div className="mt-1.5" style={{ fontSize: small ? 6 : 8, color: "#AEB4B0" }}>Selezione curata.</div>
          </div>
          <div className="p-2" style={{ background: "#1D2124", borderLeft: "1px solid #2B3033" }}>
            <div className="border rounded-sm" style={{ borderColor: "#6E8C7C", height: small ? 30 : 50 }} />
          </div>
        </div>
        <div className="px-2 py-1.5 flex gap-1" style={{ background: "#E9E3D6" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex-1 rounded-sm border" style={{ background: "#14171A", borderColor: "#D9D2C1", height: small ? 12 : 20 }} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className={
        "rounded-lg overflow-hidden border border-slate-200 shadow-sm " +
        (small ? "text-[8px]" : "text-xs")
      }
      style={{ ...cssVars, background: "var(--c-secondary)", color: "var(--c-text)" }}
    >
      {/* Header */}
      <div
        className={
          "flex items-center justify-between px-2 py-1.5 " +
          (style === "luxury" || style === "industrial" ? "bg-black text-white" : "")
        }
        style={
          style === "luxury" || style === "industrial"
            ? undefined
            : { background: "var(--c-primary)", color: "#fff" }
        }
      >
        <div className="flex items-center gap-1.5">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className={small ? "h-3 w-3 rounded" : "h-4 w-4 rounded object-cover bg-white"} />
          ) : (
            <div className={small ? "h-3 w-3 rounded bg-white/40" : "h-4 w-4 rounded bg-white/40"} />
          )}
          <span className={"font-bold truncate " + (style === "classic" || style === "heritage" || style === "editorial" ? "font-serif" : "")}>
            {studioName || "Nome Studio"}
          </span>
        </div>
        <div className="flex gap-1 opacity-80">
          <span>•</span><span>•</span><span>•</span>
        </div>
      </div>
      {/* Hero */}
      <div className={"px-2 " + (small ? "py-2" : "py-3")}>
        <div
          className={
            "font-bold leading-tight " +
            (style === "classic" || style === "heritage" || style === "editorial" ? "font-serif" : "") +
            (style === "modern" || style === "vibrant" || style === "industrial" ? " uppercase tracking-wider" : "")
          }
          style={{ color: "var(--c-primary)" }}
        >
          {style === "luxury" ? "Immobili d'eccellenza" :
           style === "blueprint" ? "Ingegneria di precisione" :
           style === "heritage" ? "Tradizione e Diritto" :
           "Il tuo partner professionale"}
        </div>
        <div className="mt-1 text-[9px] opacity-70">
          Servizi su misura per ogni esigenza.
        </div>
      </div>
      {/* Card row */}
      <div className={"px-2 pb-2 grid gap-1 " + (style === "portfolio" || style === "vibrant" ? "grid-cols-3" : "grid-cols-2")}>
        {[0, 1, 2].slice(0, style === "portfolio" || style === "vibrant" ? 3 : 2).map((i) => (
          <div
            key={i}
            className={"h-6 " + (style === "industrial" || style === "modern" ? "rounded-none" : "rounded-md")}
            style={{
              background: i === 0 ? "var(--c-primary)" : "var(--c-secondary)",
              opacity: i === 0 ? 1 : 0.7,
              border: i > 0 ? `1px solid var(--c-primary)` : "none",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ==================== Steps ====================
function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="site-wizard-steps">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div
              className={
                "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold " +
                (done ? "bg-emerald-100 text-emerald-800" :
                 active ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-500")
              }
              data-testid={`site-wizard-step-${i}`}
              aria-current={active}
            >
              {done ? <CheckCircle size={12} weight="fill" /> : <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-white/30 text-[10px]">{i + 1}</span>}
              <span>{s}</span>
            </div>
            {i < steps.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ==================== Main Wizard ====================
// Iter208.64 — Wrapper "chrome browser" attorno al TemplatePreview: mostra
// una URL bar realistica con il dominio scelto dal pro, per aumentare
// l'effetto "wow" (il pro vede il suo dominio dentro il preview).
// Iter208.75 — Supporto viewport responsive: desktop (full), tablet (max-w-md),
// mobile (max-w-[280px]). Il container esterno resta full-width per evitare
// jump di layout; è il chrome stesso che si restringe centrato.
const VIEWPORT_MAX_W = {
  desktop: "max-w-full",
  tablet:  "max-w-md",
  mobile:  "max-w-[280px]",
};
function BrowserChrome({ domain, children, testId, viewport = "desktop" }) {
  const url = domain ? `https://${domain}` : "https://iltuosito.demo";
  const maxW = VIEWPORT_MAX_W[viewport] || VIEWPORT_MAX_W.desktop;
  return (
    <div className="w-full flex justify-center">
      <div
        className={`rounded-lg border border-slate-200 bg-slate-100 shadow-md overflow-hidden w-full ${maxW} transition-[max-width] duration-300`}
        data-testid={testId || "site-wizard-browser-chrome"}
        data-viewport={viewport}
      >
        <div className="flex items-center gap-2 bg-slate-200 border-b border-slate-300 px-2 py-1.5">
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="flex-1 mx-1 h-6 rounded-md bg-white border border-slate-300 flex items-center px-2 gap-1.5 min-w-0" data-testid="site-wizard-browser-url">
            <Lock size={10} weight="fill" className="text-emerald-600 shrink-0" />
            <span className="text-[11px] font-mono text-slate-700 truncate">{url}</span>
          </div>
        </div>
        <div className="bg-white">{children}</div>
      </div>
    </div>
  );
}

// Iter208.75 — Toggle Desktop / Tablet / Mobile per l'anteprima.
function ViewportToggle({ value, onChange, testIdPrefix = "site-wizard-viewport" }) {
  const options = [
    { key: "desktop", label: "Desktop", Icon: Desktop },
    { key: "tablet",  label: "Tablet",  Icon: DeviceTablet },
    { key: "mobile",  label: "Mobile",  Icon: DeviceMobile },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 shadow-sm"
      role="tablist"
      aria-label="Anteprima responsive"
      data-testid={`${testIdPrefix}-toggle`}
    >
      {options.map(({ key, label, Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            title={`Anteprima ${label}`}
            onClick={() => onChange(key)}
            data-testid={`${testIdPrefix}-${key}`}
            className={
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors " +
              (active
                ? "bg-purple-600 text-white"
                : "text-slate-600 hover:bg-slate-100")
            }
          >
            <Icon size={12} weight={active ? "fill" : "bold"} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function SiteWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Iter208.64 — dominio scelto in MyDomain (?domain=...): usato per il chrome
  // del browser mockup nell'anteprima live.
  const [customDomain, setCustomDomain] = useState("");
  useEffect(() => {
    const fromQuery = (searchParams.get("domain") || "").trim().toLowerCase();
    if (fromQuery) {
      setCustomDomain(fromQuery);
      return;
    }
    // Fallback: prova a leggere dal progress salvato del pro
    api.get("/me/my-domain-progress")
      .then((r) => {
        const dv = r.data?.progress?.domain_value;
        if (dv) setCustomDomain(dv);
      })
      .catch(() => {});
  }, [searchParams]);

  const [current, setCurrent] = useState(0);
  const [studioName, setStudioName] = useState("");
  const [category, setCategory] = useState("agenzia");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoLoading, setLogoLoading] = useState(false);
  // Iter208.72 — Mini-form inline per personalizzare la generazione AI del logo.
  const [showLogoForm, setShowLogoForm] = useState(false);
  const [logoFormName, setLogoFormName] = useState("");
  const [logoFormDesc, setLogoFormDesc] = useState("");
  const [logoFormColor, setLogoFormColor] = useState("");
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [templateId, setTemplateId] = useState("");
  const [customSections, setCustomSections] = useState([]);
  const [seo, setSeo] = useState({ title: "", description: "", keywords: [] });
  const [seoSuggestions, setSeoSuggestions] = useState({ title: "", description: "", keywords: [] });
  const [publishing, setPublishing] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  // Iter208.75 — Viewport dell'anteprima live (desktop | tablet | mobile).
  const [previewViewport, setPreviewViewport] = useState("desktop");
  // Iter208.81 — Template selezionato per anteprima live full-screen (modal).
  const [previewingTpl, setPreviewingTpl] = useState(null); // { id, label }

  const steps = ["Logo", "Colori", "Template", "Sezioni", "SEO", "Pubblica"];
  const templates = TEMPLATES[category] || [];
  const currentTemplate = templates.find((t) => t.id === templateId) || templates[0];

  // Bootstrap: carica config esistente
  useEffect(() => {
    api.get("/site/config").then((r) => {
      const cfg = r.data?.site_config || {};
      setStudioName(r.data?.studio_name || "");
      const cat = cfg.category || r.data?.professional_category || "agenzia";
      setCategory(["agenzia", "tecnica", "notarile"].includes(cat) ? cat : "agenzia");
      if (cfg.logo_url) setLogoUrl(cfg.logo_url);
      if (cfg.colors) setColors({ ...DEFAULT_COLORS, ...cfg.colors });
      if (cfg.template_id) setTemplateId(cfg.template_id);
      if (cfg.custom_sections) setCustomSections(cfg.custom_sections);
      if (cfg.seo) setSeo({ title: "", description: "", keywords: [], ...cfg.seo });
      if (cfg.published_at) setPublishedAt(cfg.published_at);
    }).catch(() => {});
    // Carica suggerimenti SEO in parallelo
    api.get("/site/seo-suggest").then((r) => setSeoSuggestions(r.data || {})).catch(() => {});
  }, []);

  const saveConfig = async (partial) => {
    try {
      await api.patch("/site/config", partial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  // ---------- Step 1: Logo ----------
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setLogoUrl(dataUrl);
      await saveConfig({ logo_url: dataUrl, category });
    };
    reader.readAsDataURL(f);
  };
  const generateAi = async (opts = {}) => {
    const nameFinal = (opts.studioName ?? studioName).trim();
    if (!nameFinal) { toast.error("Inserisci il nome studio"); return; }
    setLogoLoading(true);
    try {
      const r = await api.post("/site/logo-generate", {
        studio_name: nameFinal,
        category,
        description: (opts.description || "").trim(),
        primary_color: opts.primary_color || "",
      }, { timeout: 90000 });
      setLogoUrl(r.data.logo_url);
      await saveConfig({ logo_url: r.data.logo_url, category });
      toast.success("Logo generato");
      setShowLogoForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generazione fallita");
    } finally { setLogoLoading(false); }
  };

  // ---------- Step 2: Colori ----------
  const setColor = (k, v) => setColors((c) => ({ ...c, [k]: v }));
  const saveColors = () => saveConfig({ colors });

  // ---------- Step 3: Template ----------
  const chooseTemplate = (tid) => {
    setTemplateId(tid);
    saveConfig({ template_id: tid, category });
  };

  // ---------- Step 4: Pubblica ----------
  const publish = async () => {
    setPublishing(true);
    try {
      const r = await api.post("/site/publish");
      setPublishedAt(r.data.published_at);
      toast.success("Sito pubblicato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pubblicazione");
    } finally { setPublishing(false); }
  };

  const goNext = () => {
    if (current === 1) saveColors();
    if (current === 4) saveConfig({ seo });
    if (current < steps.length - 1) setCurrent(current + 1);
  };
  const goPrev = () => current > 0 && setCurrent(current - 1);

  const previewProps = { colors, studioName, logoUrl };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="site-wizard">
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <CaretLeft size={12} /> Torna indietro
        </button>
        <h1 className="text-2xl font-display font-semibold text-slate-900 mt-1">Crea il tuo sito pubblico</h1>
        <p className="text-sm text-slate-500">Categoria: <strong className="capitalize">{CATEGORY_LABEL[category] || category}</strong></p>
        {customDomain && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-purple-800 bg-purple-50 border border-purple-200 rounded-full px-3 py-1" data-testid="site-wizard-domain-chip">
            <Lock size={11} weight="fill" className="text-emerald-600" />
            Il tuo sito sarà pubblicato su <span className="font-mono">{customDomain}</span>
          </div>
        )}
      </div>

      <StepIndicator steps={steps} current={current} />

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        {/* STEP 1 — Logo */}
        {current === 0 && (
          <div className="space-y-5" data-testid="site-wizard-step-logo">
            <div className="flex items-center gap-2">
              <UploadSimple size={20} weight="bold" className="text-purple-600" />
              <h2 className="text-lg font-semibold">Logo dello studio</h2>
            </div>
            <p className="text-sm text-slate-600">Carica un tuo logo (PNG/JPG, sfondo trasparente consigliato) oppure genera un placeholder AI basato sul nome studio.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center">
                <input type="file" id="logo-upload" hidden accept="image/*" onChange={onUpload} data-testid="site-wizard-logo-upload" />
                <label htmlFor="logo-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
                  <UploadSimple size={14} /> Carica dal computer
                </label>
                <div className="text-[11px] text-slate-500 mt-2">Max 3 MB</div>
              </div>
              <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-6 text-center">
                <Button
                  onClick={() => {
                    // Iter208.72 — apre mini-form invece di generare subito
                    setLogoFormName(studioName || "");
                    setLogoFormDesc("");
                    setLogoFormColor(colors?.primary || "#7C3AED");
                    setShowLogoForm(true);
                  }}
                  disabled={logoLoading}
                  className="ht-gradient border-0 text-white"
                  data-testid="site-wizard-logo-generate"
                >
                  <MagicWand size={14} className="mr-1" />
                  {logoLoading ? "Genero…" : "Genera con AI"}
                </Button>
                <div className="text-[11px] text-slate-600 mt-2">Genera un logo con AI personalizzato per il tuo studio</div>
              </div>
            </div>

            {/* Iter208.72 — Mini-form inline per personalizzare la generazione AI */}
            {showLogoForm && (
              <div
                className="rounded-xl border border-purple-300 bg-white p-4 sm:p-5 space-y-3 shadow-sm"
                data-testid="site-wizard-logo-form"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
                    <MagicWand size={14} className="text-purple-600" weight="bold" />
                    Personalizza la generazione
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLogoForm(false)}
                    className="text-xs text-slate-500 hover:text-slate-800"
                    data-testid="site-wizard-logo-form-close"
                  >
                    Annulla
                  </button>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                    Nome azienda / studio
                  </label>
                  <Input
                    type="text"
                    value={logoFormName}
                    onChange={(e) => setLogoFormName(e.target.value)}
                    placeholder="Es. Studio Rossi Immobiliare"
                    data-testid="site-wizard-logo-form-name"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                    Settore / descrizione breve <span className="text-slate-400 font-normal">(opzionale)</span>
                  </label>
                  <Input
                    type="text"
                    value={logoFormDesc}
                    onChange={(e) => setLogoFormDesc(e.target.value)}
                    placeholder="Es. Agenzia immobiliare di lusso a Milano"
                    data-testid="site-wizard-logo-form-desc"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                    Colore primario
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={logoFormColor || "#7C3AED"}
                      onChange={(e) => setLogoFormColor(e.target.value)}
                      className="w-12 h-10 rounded-md border border-slate-200 cursor-pointer bg-white"
                      data-testid="site-wizard-logo-form-color"
                    />
                    <Input
                      type="text"
                      value={logoFormColor || ""}
                      onChange={(e) => setLogoFormColor(e.target.value)}
                      placeholder="#7C3AED"
                      className="max-w-[140px] font-mono text-sm"
                    />
                    <span className="text-[11px] text-slate-500">
                      {colors?.primary ? "Pre-compilato dallo step colori" : "Viola brand di default"}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end pt-2 border-t">
                  <Button
                    onClick={() => generateAi({
                      studioName: logoFormName,
                      description: logoFormDesc,
                      primary_color: logoFormColor,
                    })}
                    disabled={logoLoading || !logoFormName.trim()}
                    className="ht-gradient border-0 text-white"
                    data-testid="site-wizard-logo-form-submit"
                  >
                    <MagicWand size={14} className="mr-1" />
                    {logoLoading ? "Genero…" : "Genera logo"}
                  </Button>
                </div>
              </div>
            )}

            {logoUrl && (
              <div className="pt-4 border-t">
                <div className="text-xs font-semibold text-slate-600 mb-2">Anteprima logo attuale</div>
                <div className="w-40 h-40 rounded-xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden shadow-sm">
                  <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Colori */}
        {current === 1 && (
          <div className="space-y-5" data-testid="site-wizard-step-colors">
            <div className="flex items-center gap-2">
              <Palette size={20} weight="bold" className="text-purple-600" />
              <h2 className="text-lg font-semibold">Palette colori</h2>
            </div>
            <p className="text-sm text-slate-600">Scegli i colori principali del tuo sito. L'anteprima a destra si aggiorna in tempo reale.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[
                  { k: "primary",   label: "Colore primario (brand)" },
                  { k: "secondary", label: "Colore secondario (sfondo)" },
                  { k: "text",      label: "Colore testo" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={colors[f.k]}
                        onChange={(e) => setColor(f.k, e.target.value)}
                        className="w-14 h-10 rounded cursor-pointer border border-slate-300"
                        data-testid={`site-wizard-color-${f.k}`}
                      />
                      <Input value={colors[f.k]} onChange={(e) => setColor(f.k, e.target.value)} className="flex-1 font-mono text-sm" />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-600">Anteprima live</div>
                  <ViewportToggle
                    value={previewViewport}
                    onChange={setPreviewViewport}
                    testIdPrefix="site-wizard-viewport-colors"
                  />
                </div>
                <BrowserChrome domain={customDomain} testId="site-wizard-preview-colors" viewport={previewViewport}>
                  <TemplatePreview
                    template={currentTemplate || templates[0]}
                    {...previewProps}
                  />
                </BrowserChrome>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Template */}
        {current === 2 && (
          <div className="space-y-5" data-testid="site-wizard-step-template">
            <div className="flex items-center gap-2">
              <Layout size={20} weight="bold" className="text-purple-600" />
              <h2 className="text-lg font-semibold">Scegli il template</h2>
            </div>
            <p className="text-sm text-slate-600">5 layout diversi ottimizzati per <strong>{CATEGORY_LABEL[category]}</strong>. I colori scelti al passo precedente sono già applicati.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((t) => {
                const active = templateId === t.id;
                return (
                  <div
                    key={t.id}
                    className={
                      "relative text-left p-3 rounded-xl border-2 transition-all cursor-pointer " +
                      (active ? "border-purple-500 shadow-md bg-purple-50/40" : "border-slate-200 hover:border-slate-300")
                    }
                    onClick={() => chooseTemplate(t.id)}
                    data-testid={`site-wizard-tpl-${t.id}`}
                  >
                    <TemplatePreview template={t} {...previewProps} />
                    <div className="mt-2 flex items-center justify-between">
                      <div className="font-semibold text-sm">{t.label}</div>
                      {active && <CheckCircle size={16} weight="fill" className="text-purple-600" />}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{t.desc}</div>
                    {/* Iter208.81 — Preview live full-screen */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPreviewingTpl({ id: t.id, label: t.label }); }}
                      className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
                      data-testid={`site-wizard-tpl-preview-${t.id}`}
                    >
                      <MagnifyingGlass size={11} weight="bold"/> Anteprima live
                    </button>
                    {/* Iter209n — "Prova questo": apre /site-preview?template=X
                        in nuova scheda per confrontare due template affiancati. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/site-preview?template=${encodeURIComponent(t.id)}`, "_blank", "noopener");
                      }}
                      className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-1.5 rounded-md border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800 transition-colors"
                      data-testid={`site-wizard-tpl-try-${t.id}`}
                      title="Apri l'anteprima di questo template in una nuova scheda per confrontarlo con altri"
                    >
                      <ArrowSquareOut size={11} weight="bold" /> Prova questo (nuova scheda)
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4 — Sezioni personalizzate */}
        {current === 3 && (
          <CustomSectionsEditor
            sections={customSections}
            onChange={(next) => {
              setCustomSections(next);
              saveConfig({ custom_sections: next });
            }}
            category={category}
          />
        )}

        {/* STEP 5 — SEO */}
        {current === 4 && (
          <SeoStep
            seo={seo}
            setSeo={setSeo}
            suggestions={seoSuggestions}
            onSave={() => saveConfig({ seo })}
          />
        )}

        {/* STEP 6 — Pubblica */}
        {current === 5 && (
          <div className="space-y-5" data-testid="site-wizard-step-publish">
            <div className="flex items-center gap-2">
              <Rocket size={20} weight="bold" className="text-purple-600" />
              <h2 className="text-lg font-semibold">Riepilogo e pubblicazione</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 text-sm">
                <SummaryRow label="Nome studio" value={studioName || "—"} />
                <SummaryRow label="Categoria" value={CATEGORY_LABEL[category]} />
                <SummaryRow label="Logo" value={logoUrl ? "Caricato ✓" : "Nessun logo"} />
                <SummaryRow label="Colori" value={
                  <div className="flex gap-1">
                    <span className="w-5 h-5 rounded border" style={{ background: colors.primary }} title="primary" />
                    <span className="w-5 h-5 rounded border" style={{ background: colors.secondary }} title="secondary" />
                    <span className="w-5 h-5 rounded border" style={{ background: colors.text }} title="text" />
                  </div>
                } />
                <SummaryRow label="Template" value={currentTemplate?.label || "Non scelto"} />
                <SummaryRow label="Pagine" value={PAGES[category].join(" · ")} />
                <SummaryRow label="SEO Title" value={seo.title || <em className="text-slate-400">non impostato</em>} />
                <SummaryRow label="Keywords" value={(seo.keywords || []).length ? `${(seo.keywords || []).length} tag` : "—"} />
                {publishedAt && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 text-xs" data-testid="site-wizard-published">
                    Sito pubblicato il {new Date(publishedAt).toLocaleString("it-IT")}. Il collegamento al dominio custom sarà attivato nella prossima fase.
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-600">Anteprima</div>
                  <ViewportToggle
                    value={previewViewport}
                    onChange={setPreviewViewport}
                    testIdPrefix="site-wizard-viewport-publish"
                  />
                </div>
                <BrowserChrome domain={customDomain} testId="site-wizard-preview-publish" viewport={previewViewport}>
                  <TemplatePreview template={currentTemplate || templates[0]} {...previewProps} />
                </BrowserChrome>
              </div>
            </div>
            <Button
              onClick={publish}
              disabled={publishing || !templateId}
              className="ht-gradient border-0 text-white w-full sm:w-auto"
              data-testid="site-wizard-publish"
            >
              <Rocket size={14} className="mr-2" />
              {publishing ? "Pubblico…" : publishedAt ? "Ripubblica sito" : "Pubblica sito"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/site-preview")}
              className="ml-2"
              data-testid="site-wizard-preview"
            >
              Apri anteprima live →
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between pt-6 mt-6 border-t">
          <Button variant="outline" onClick={goPrev} disabled={current === 0} data-testid="site-wizard-back">
            <CaretLeft size={14} className="mr-1" /> Indietro
          </Button>
          {current < steps.length - 1 ? (
            <Button onClick={goNext} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="site-wizard-next">
              Avanti <CaretRight size={14} className="ml-1" />
            </Button>
          ) : null}
        </div>
      </div>

      {/* Iter208.81 — Modal anteprima live del template */}
      {previewingTpl && (
        <TemplateLivePreviewModal
          templateId={previewingTpl.id}
          templateLabel={previewingTpl.label}
          slug={user?.id}
          onClose={() => setPreviewingTpl(null)}
        />
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-slate-100 last:border-b-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
}

// ==================== CustomSectionsEditor (Iter208.68) ====================
// Iter208.74 — Hint helper (native `title` tooltip via icona `?` cliccabile).
function Hint({ text, testId }) {
  return (
    <span
      title={text}
      data-testid={testId}
      role="img"
      aria-label={text}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help hover:bg-slate-300 select-none"
    >
      ?
    </span>
  );
}

const SECTION_PAGES_BY_CAT = {
  agenzia: [["home","Home"],["annunci","Annunci"],["about","Chi siamo"],["contatti","Contatti"]],
  tecnica: [["home","Home"],["about","Chi siamo"],["contatti","Contatti"]],
  notarile:[["about","Chi siamo"],["prenota","Prenota"],["contatti","Contatti"]],
};
const EMPTY_ITEM = {
  testimonials: { name: "", role: "", quote: "" },
  services:     { title: "", description: "" },
  gallery:      { image_url: "", caption: "" },
};

function CustomSectionsEditor({ sections, onChange, category }) {
  const pages = SECTION_PAGES_BY_CAT[category] || SECTION_PAGES_BY_CAT.agenzia;

  const addSection = (type) => {
    onChange([...(sections || []), {
      id: `sec-${Date.now()}`,
      type,
      title: type === "testimonials" ? "Testimonianze" : type === "services" ? "Servizi" : "Gallery",
      page: pages[0][0],
      items: [{ ...(EMPTY_ITEM[type]) }],
    }]);
  };
  const removeSection = (id) => onChange((sections || []).filter((s) => s.id !== id));
  const patchSection = (id, patch) => onChange((sections || []).map((s) => s.id === id ? { ...s, ...patch } : s));
  const patchItem = (sid, idx, patch) => {
    const s = sections.find((x) => x.id === sid);
    if (!s) return;
    const items = s.items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    patchSection(sid, { items });
  };
  const addItem = (sid) => {
    const s = sections.find((x) => x.id === sid);
    if (!s) return;
    patchSection(sid, { items: [...(s.items || []), { ...(EMPTY_ITEM[s.type]) }] });
  };
  const removeItem = (sid, idx) => {
    const s = sections.find((x) => x.id === sid);
    if (!s) return;
    patchSection(sid, { items: s.items.filter((_, i) => i !== idx) });
  };
  // Iter208.74 — Upload diretto immagine gallery (base64 dataUrl, coerente con Logo step)
  const onGalleryUpload = (e, sid, idx) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => patchItem(sid, idx, { image_url: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <div className="space-y-4" data-testid="site-wizard-step-sections">
      <div className="flex items-center gap-2">
        <Layout size={20} weight="bold" className="text-purple-600" />
        <h2 className="text-lg font-semibold">Sezioni personalizzate</h2>
      </div>
      <p className="text-sm text-slate-600">Aggiungi contenuti opzionali al tuo sito senza modificare il template. Ogni sezione può essere assegnata a una pagina.</p>

      {/* Iter208.74 — Banner opzionalità step */}
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900"
        data-testid="site-wizard-sections-optional-banner"
      >
        <Info size={16} weight="bold" className="text-amber-500 mt-0.5 shrink-0" />
        <p>Questi contenuti sono opzionali. Puoi saltare questo step e aggiungere sezioni in seguito dalla gestione sito.</p>
      </div>

      {/* Iter208.73 — Riquadro "Pagine incluse" (readonly) */}
      <div
        className="rounded-lg border border-slate-200 bg-white p-4"
        data-testid="site-wizard-included-pages"
      >
        <div className="text-xs font-semibold text-slate-700 mb-2 inline-flex items-center gap-1.5">
          <Layout size={13} weight="bold" className="text-purple-600" />
          Pagine incluse nel tuo sito
        </div>
        <div className="flex flex-wrap gap-2">
          {pages.map(([slug, label]) => (
            <span
              key={slug}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
              data-testid={`site-wizard-included-page-${slug}`}
            >
              <CheckCircle size={12} weight="fill" className="text-emerald-500" />
              {label}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2 italic">
          Le sezioni qui sotto sono contenuti opzionali aggiuntivi che puoi inserire in una qualsiasi delle pagine base.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => addSection("testimonials")} data-testid="site-wizard-sec-add-testimonials">+ Testimonianze</Button>
          <Hint testId="site-wizard-hint-testimonials" text="Aggiungi recensioni dei tuoi clienti (nome, ruolo e citazione) per costruire fiducia sul tuo sito." />
        </div>
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => addSection("services")}     data-testid="site-wizard-sec-add-services">+ Servizi</Button>
          <Hint testId="site-wizard-hint-services" text="Elenca i servizi che offri con un titolo e una breve descrizione. Utile per far capire subito cosa fai." />
        </div>
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => addSection("gallery")}      data-testid="site-wizard-sec-add-gallery">+ Gallery</Button>
          <Hint testId="site-wizard-hint-gallery" text="Mostra immagini di progetti, immobili o momenti dello studio. Puoi caricare i file direttamente dal computer." />
        </div>
      </div>

      {(sections || []).length === 0 && (
        <div className="text-center py-8 text-sm text-slate-500 italic border border-dashed rounded-lg">
          Nessuna sezione personalizzata. Clicca su un pulsante sopra per iniziare.
        </div>
      )}

      <div className="space-y-4">
        {(sections || []).map((s) => (
          <div key={s.id} className="rounded-lg border border-slate-200 p-4 bg-slate-50/60" data-testid={`site-wizard-sec-${s.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-800">{s.type}</span>
              <Input
                value={s.title}
                onChange={(e) => patchSection(s.id, { title: e.target.value })}
                className="flex-1 h-8"
                placeholder="Titolo sezione"
              />
              <select
                value={s.page}
                onChange={(e) => patchSection(s.id, { page: e.target.value })}
                className="h-8 px-2 rounded border border-slate-300 bg-white text-xs"
                data-testid={`site-wizard-sec-page-${s.id}`}
              >
                {pages.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
              </select>
              <Hint testId={`site-wizard-hint-page-${s.id}`} text="Scegli in quale pagina base del tuo sito verrà mostrata questa sezione." />
              <Button variant="ghost" size="sm" onClick={() => removeSection(s.id)} className="text-red-600" data-testid={`site-wizard-sec-remove-${s.id}`}>Rimuovi</Button>
            </div>

            <div className="space-y-2">
              {s.items.map((it, i) => (
                <div key={i} className="p-3 bg-white border border-slate-200 rounded flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {s.type === "testimonials" && (
                      <>
                        <Input placeholder="Nome" value={it.name || ""} onChange={(e) => patchItem(s.id, i, { name: e.target.value })} />
                        <Input placeholder="Ruolo (opzionale)" value={it.role || ""} onChange={(e) => patchItem(s.id, i, { role: e.target.value })} />
                        <Input placeholder="Citazione" value={it.quote || ""} onChange={(e) => patchItem(s.id, i, { quote: e.target.value })} className="sm:col-span-2" />
                      </>
                    )}
                    {s.type === "services" && (
                      <>
                        <Input placeholder="Titolo servizio" value={it.title || ""} onChange={(e) => patchItem(s.id, i, { title: e.target.value })} />
                        <Input placeholder="Descrizione breve" value={it.description || ""} onChange={(e) => patchItem(s.id, i, { description: e.target.value })} />
                      </>
                    )}
                    {s.type === "gallery" && (
                      <>
                        <div className="sm:col-span-2">
                          {it.image_url ? (
                            <div className="flex items-center gap-2">
                              <img src={it.image_url} alt="preview" className="h-14 w-14 rounded object-cover border border-slate-200" data-testid={`site-wizard-gallery-preview-${s.id}-${i}`} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => patchItem(s.id, i, { image_url: "" })}
                                data-testid={`site-wizard-gallery-remove-image-${s.id}-${i}`}
                              >
                                Sostituisci
                              </Button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                id={`gallery-upload-${s.id}-${i}`}
                                data-testid={`site-wizard-gallery-upload-${s.id}-${i}`}
                                onChange={(e) => onGalleryUpload(e, s.id, i)}
                              />
                              <label
                                htmlFor={`gallery-upload-${s.id}-${i}`}
                                className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                              >
                                <UploadSimple size={12} weight="bold" /> Carica immagine
                              </label>
                              <span className="ml-2 text-[11px] text-slate-500">PNG/JPG, max 3 MB</span>
                            </>
                          )}
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-1">
                          <Input
                            placeholder="Didascalia (opzionale)"
                            value={it.caption || ""}
                            onChange={(e) => patchItem(s.id, i, { caption: e.target.value })}
                            className="flex-1"
                            data-testid={`site-wizard-gallery-caption-${s.id}-${i}`}
                          />
                          <Hint testId={`site-wizard-hint-caption-${s.id}-${i}`} text="Testo mostrato sotto l'immagine. Utile per contesto e per la SEO delle immagini." />
                        </div>
                      </>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeItem(s.id, i)} className="text-red-500 shrink-0">✕</Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem(s.id)} data-testid={`site-wizard-sec-item-add-${s.id}`}>
                + Aggiungi voce
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ==================== SeoStep (Iter208.69) ====================
function SeoStep({ seo, setSeo, suggestions, onSave }) {
  const [kwInput, setKwInput] = React.useState("");
  const activeKws = seo.keywords || [];
  const suggestedKws = (suggestions.keywords || []).filter((k) => !activeKws.includes(k));

  const addKeyword = (k) => {
    const v = (k || "").trim().toLowerCase();
    if (!v) return;
    if (activeKws.includes(v)) return;
    setSeo((s) => ({ ...s, keywords: [...activeKws, v].slice(0, 20) }));
    setKwInput("");
  };
  const removeKeyword = (k) => setSeo((s) => ({ ...s, keywords: activeKws.filter((x) => x !== k) }));
  const applySuggestion = (field) => {
    const val = suggestions[field];
    if (val) setSeo((s) => ({ ...s, [field]: val }));
  };

  const titleLen = (seo.title || "").length;
  const descLen = (seo.description || "").length;

  return (
    <div className="space-y-5" data-testid="site-wizard-step-seo">
      <div className="flex items-center gap-2">
        <MagicWand size={20} weight="bold" className="text-purple-600" />
        <h2 className="text-lg font-semibold">Ottimizzazione SEO</h2>
      </div>
      <p className="text-sm text-slate-600">Aiuta Google a indicizzare correttamente il tuo sito. I suggerimenti qui sotto sono già personalizzati in base alla tua categoria e alla città della sede.</p>

      {/* TITLE */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-slate-700">Titolo sito (SEO Title)</label>
          <span className={"text-[10px] " + (titleLen > 60 ? "text-red-600" : "text-slate-400")}>{titleLen}/60</span>
        </div>
        <Input
          value={seo.title || ""}
          onChange={(e) => setSeo((s) => ({ ...s, title: e.target.value.slice(0, 70) }))}
          onBlur={onSave}
          placeholder={suggestions.title || "Es. Studio Rossi — Agenzia Immobiliare a Milano"}
          data-testid="site-wizard-seo-title"
        />
        {suggestions.title && seo.title !== suggestions.title && (
          <button
            type="button"
            onClick={() => applySuggestion("title")}
            className="mt-1 text-[11px] text-purple-700 font-semibold hover:underline"
            data-testid="site-wizard-seo-title-apply"
          >
            Usa suggerimento: "{suggestions.title}"
          </button>
        )}
      </div>

      {/* DESCRIPTION */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-slate-700">Meta description</label>
          <span className={"text-[10px] " + (descLen > 160 ? "text-red-600" : "text-slate-400")}>{descLen}/160</span>
        </div>
        <textarea
          value={seo.description || ""}
          onChange={(e) => setSeo((s) => ({ ...s, description: e.target.value.slice(0, 180) }))}
          onBlur={onSave}
          rows={3}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder={suggestions.description || "Descrivi in 1-2 frasi cosa fai e per chi."}
          data-testid="site-wizard-seo-description"
        />
        {suggestions.description && seo.description !== suggestions.description && (
          <button
            type="button"
            onClick={() => applySuggestion("description")}
            className="mt-1 text-[11px] text-purple-700 font-semibold hover:underline"
            data-testid="site-wizard-seo-description-apply"
          >
            Usa suggerimento
          </button>
        )}
      </div>

      {/* KEYWORDS */}
      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1 block">Parole chiave (max 20)</label>
        <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
          {activeKws.length === 0 && (
            <span className="text-[11px] text-slate-400 italic">Nessuna parola chiave selezionata</span>
          )}
          {activeKws.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-purple-600 text-white text-[11px] font-semibold"
              data-testid={`site-wizard-seo-kw-active-${k}`}
            >
              {k}
              <button
                type="button"
                onClick={() => removeKeyword(k)}
                className="ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-white/20"
                aria-label={`Rimuovi ${k}`}
              >×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addKeyword(kwInput); }
            }}
            placeholder="Aggiungi una parola chiave e premi Invio"
            className="flex-1"
            data-testid="site-wizard-seo-kw-input"
          />
          <Button variant="outline" onClick={() => addKeyword(kwInput)} data-testid="site-wizard-seo-kw-add">
            Aggiungi
          </Button>
        </div>
        {suggestedKws.length > 0 && (
          <div>
            <div className="text-[11px] text-slate-500 mb-1">Suggerite (clicca per aggiungerle):</div>
            <div className="flex flex-wrap gap-1.5">
              {suggestedKws.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => addKeyword(k)}
                  className="text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-purple-100 hover:text-purple-800 text-slate-600 transition-colors"
                  data-testid={`site-wizard-seo-kw-suggest-${k.replace(/\s/g, "-")}`}
                >
                  + {k}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* GOOGLE SEARCH PREVIEW */}
      <div className="border-t pt-4">
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Anteprima ricerca Google</div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 max-w-2xl">
          <div className="text-xs text-emerald-700">https://housetag.it/studio/…</div>
          <div className="text-lg text-blue-800 mt-0.5 truncate">{seo.title || suggestions.title || "Titolo del sito"}</div>
          <div className="text-sm text-slate-600 mt-1 line-clamp-2">
            {seo.description || suggestions.description || "Meta description mostrata nei risultati Google."}
          </div>
        </div>
      </div>
    </div>
  );
}

